/**
 * Analytics Dashboard Page
 * Version: 6.2.0
 * 
 * Trading performance analytics with charts and metrics
 */
import { useState, useMemo } from 'react'
import { LineChart, BarChart, CandlestickChart } from '../components/TradingViewCharts'
import { TradingChartWidget } from '../components/TradingChart'
import { MetricCard, RiskGauge, StatRow, Sparkline, RatioDisplay } from '../components/StatsCards'
import { Histogram, SectorBreakdown, TimeDistribution, DurationAnalysis } from '../components/TradeDistribution'
import { DateRangePicker } from '../components/DateRangePicker'
import './Analytics.css'

// Statistics Card Component
interface StatCardProps {
  label: string
  value: string | number
  change?: number
  icon: string
  color?: string
}

function StatCard({ label, value, change, icon, color = 'primary' }: StatCardProps) {
  const isPositive = change !== undefined && change >= 0
  
  return (
    <div className={`analytics-stat-card ${color}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <span className="stat-label">{label}</span>
        <span className="stat-value">{value}</span>
        {change !== undefined && (
          <span className={`stat-change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '↑' : '↓'} {Math.abs(change).toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  )
}

// Chart Container Component
interface ChartContainerProps {
  title: string
  children: React.ReactNode
  className?: string
}

function ChartContainer({ title, children, className = '' }: ChartContainerProps) {
  return (
    <div className={`chart-container ${className}`}>
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
      </div>
      <div className="chart-body">
        {children}
      </div>
    </div>
  )
}

// Metric Row Component
interface MetricRowProps {
  label: string
  value: string | number
  highlight?: boolean
}

function MetricRow({ label, value, highlight }: MetricRowProps) {
  return (
    <div className={`metric-row ${highlight ? 'highlight' : ''}`}>
      <span className="metric-label">{label}</span>
      <span className="metric-value">{value}</span>
    </div>
  )
}

// Main Analytics Page
function Analytics() {
  const [selectedBot, setSelectedBot] = useState('All Bots')
  const [chartType, setChartType] = useState<'candle' | 'line'>('candle')
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date()
  })

  // Sample data - replace with actual API data
  const stats = {
    totalPnL: '₹1,45,230',
    totalTrades: 247,
    winRate: 68.5,
    sharpeRatio: 2.34,
    maxDrawdown: -12.4,
    avgProfit: '₹588',
    avgLoss: '-₹312',
    profitFactor: 1.88,
    totalVolume: '₹45.2L',
  }

  const bots = ['All Bots', 'Breakout Bot', 'RSI Reversal', 'Trend Follower']

  // Generate sample equity data
  const equityData = useMemo(() => {
    const data = []
    let value = 100000
    for (let i = 0; i < 30; i++) {
      value += (Math.random() - 0.4) * 5000
      data.push({ time: `Day ${i + 1}`, value })
    }
    return data
  }, [dateRange])

  // Generate sample P&L bar data
  const pnlData = useMemo(() => {
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'].map(month => ({
      label: month,
      value: Math.round((Math.random() - 0.3) * 30000),
      color: undefined // Will use default green/red
    }))
  }, [dateRange])

  // Generate sample candle data
  const candleData = useMemo(() => {
    const data = []
    let price = 24500
    for (let i = 0; i < 50; i++) {
      const open = price
      const change = (Math.random() - 0.5) * 100
      const close = price + change
      const high = Math.max(open, close) + Math.random() * 50
      const low = Math.min(open, close) - Math.random() * 50
      data.push({
        time: `D${i + 1}`,
        open: Math.round(open),
        high: Math.round(high),
        low: Math.round(low),
        close: Math.round(close)
      })
      price = close
    }
    return data
  }, [dateRange])

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-left">
          <h1 className="page-title">📊 Analytics</h1>
          <p className="page-subtitle">Trading performance insights</p>
        </div>
        <div className="header-right">
          <select 
            className="bot-selector"
            value={selectedBot}
            onChange={(e) => setSelectedBot(e.target.value)}
          >
            {bots.map((bot) => (
              <option key={bot} value={bot}>{bot}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker 
        onRangeChange={(start, end) => setDateRange({ start, end })}
        initialRange={dateRange}
      />

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard 
          label="Total P&L" 
          value={stats.totalPnL} 
          change={15.2}
          icon="💰"
          color="success"
        />
        <StatCard 
          label="Total Trades" 
          value={stats.totalTrades}
          icon="📈"
          color="primary"
        />
        <StatCard 
          label="Win Rate" 
          value={`${stats.winRate}%`}
          change={3.2}
          icon="🎯"
          color="success"
        />
        <StatCard 
          label="Sharpe Ratio" 
          value={stats.sharpeRatio}
          icon="📊"
          color="primary"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="charts-row">
        <ChartContainer title="Performance Chart" className="performance-chart">
          <div className="chart-type-toggle">
            <button 
              className={`chart-type-btn ${chartType === 'candle' ? 'active' : ''}`}
              onClick={() => setChartType('candle')}
            >
              Candlestick
            </button>
            <button 
              className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
              onClick={() => setChartType('line')}
            >
              Line
            </button>
          </div>
          {chartType === 'candle' ? (
            <CandlestickChart data={candleData} height={320} />
          ) : (
            <LineChart data={equityData} color="#6366f1" height={320} />
          )}
        </ChartContainer>

        <ChartContainer title="P&L by Month" className="pnl-chart">
          <BarChart data={pnlData} height={280} />
        </ChartContainer>
      </div>

      {/* Key Metrics */}
      <ChartContainer title="Key Metrics" className="metrics-table">
        <div className="metrics-list">
          <MetricRow label="Maximum Drawdown" value={`${stats.maxDrawdown}%`} highlight />
          <MetricRow label="Profit Factor" value={stats.profitFactor} />
          <MetricRow label="Average Profit" value={stats.avgProfit} />
          <MetricRow label="Average Loss" value={stats.avgLoss} />
          <MetricRow label="Total Volume" value={stats.totalVolume} />
          <MetricRow label="Best Trade" value="+₹8,450" highlight />
          <MetricRow label="Worst Trade" value="-₹2,180" />
          <MetricRow label="Consecutive Wins" value="8" />
          <MetricRow label="Consecutive Losses" value="3" />
        </div>
      </ChartContainer>

      {/* TradingView-style Chart */}
      <ChartContainer title="NIFTY - Live Chart" className="tradingview-chart">
        <TradingChartWidget />
      </ChartContainer>

      {/* Charts Row 2 */}
      <div className="charts-row">
        <ChartContainer title="Win/Loss Distribution" className="distribution-chart">
          <div className="chart-placeholder distribution">
            <div className="pie-chart">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="var(--success)" strokeWidth="20" 
                  strokeDasharray={`${stats.winRate * 2.51} ${251 - stats.winRate * 2.51}`} 
                  transform="rotate(-90 50 50)"
                />
                <circle cx="50" cy="50" r="40" fill="none" stroke="var(--danger)" strokeWidth="20"
                  strokeDasharray={`${(100 - stats.winRate) * 2.51} 251`}
                  strokeDashoffset={`-${stats.winRate * 2.51}`}
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="pie-center">
                <span className="pie-value">{stats.winRate}%</span>
                <span className="pie-label">Win Rate</span>
              </div>
            </div>
            <div className="distribution-legend">
              <div className="legend-item">
                <span className="legend-dot success"></span>
                <span>Wins ({Math.round(stats.totalTrades * stats.winRate / 100)})</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot danger"></span>
                <span>Losses ({Math.round(stats.totalTrades * (100 - stats.winRate) / 100)})</span>
              </div>
            </div>
          </div>
        </ChartContainer>

        <ChartContainer title="Trade Distribution" className="sector-chart">
          <div className="chart-placeholder">
            <div className="sector-list">
              {[
                { name: 'NIFTY', trades: 85, pnl: '+₹45,230' },
                { name: 'BANKNIFTY', trades: 72, pnl: '+₹38,450' },
                { name: 'FINNIFTY', trades: 52, pnl: '+₹28,100' },
                { name: 'SENSEX', trades: 38, pnl: '+₹33,450' },
              ].map((sector) => (
                <div key={sector.name} className="sector-row">
                  <span className="sector-name">{sector.name}</span>
                  <div className="sector-bar">
                    <div 
                      className="sector-fill"
                      style={{ width: `${(sector.trades / 85) * 100}%` }}
                    />
                  </div>
                  <span className="sector-trades">{sector.trades}</span>
                  <span className="sector-pnl">{sector.pnl}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartContainer>
      </div>

      {/* Advanced Statistics */}
      <div className="analytics-section">
        <h2 className="section-title">📊 Advanced Metrics</h2>
        
        <div className="stats-grid-advanced">
          {/* Risk Metrics */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">⚠️ Risk Metrics</h3>
            </div>
            <div className="stats-section-body">
              <div className="risk-gauges">
                <RiskGauge value={12.4} max={50} label="Max Drawdown" />
                <RiskGauge value={35} max={100} label="Risk Score" />
                <RiskGauge value={2.1} max={5} label="Sharpe Ratio" unit="x" />
              </div>
            </div>
          </div>

          {/* Performance Ratios */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">📈 Performance Ratios</h3>
            </div>
            <div className="stats-section-body">
              <MetricCard
                label="Sortino Ratio"
                value="1.87"
                icon="📉"
                trend="up"
                change={12.5}
                variant="success"
              />
              <MetricCard
                label="Calmar Ratio"
                value="0.95"
                icon="🏔️"
                variant="warning"
                tooltip="Return / Max Drawdown"
              />
              <MetricCard
                label="Expectancy"
                value="₹276"
                subValue="Per trade"
                icon="🎯"
                variant="success"
              />
            </div>
          </div>

          {/* Trade Analysis */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">📊 Trade Analysis</h3>
            </div>
            <div className="stats-section-body">
              <RatioDisplay 
                label="Win Rate"
                wins={Math.round(stats.totalTrades * stats.winRate / 100)}
                losses={Math.round(stats.totalTrades * (100 - stats.winRate) / 100)}
              />
              <StatRow 
                label="Best Trade"
                value="+₹8,450"
                variant="success"
              />
              <StatRow 
                label="Worst Trade"
                value="-₹2,180"
                variant="danger"
              />
              <StatRow 
                label="Avg Win"
                value="₹588"
                subLabel="Avg loss: ₹312"
              />
            </div>
          </div>

          {/* Streaks */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">🔥 Streaks</h3>
            </div>
            <div className="stats-section-body">
              <MetricCard
                label="Current Win Streak"
                value="4"
                icon="🔥"
                trend="up"
                variant="success"
              />
              <MetricCard
                label="Best Win Streak"
                value="8"
                icon="⭐"
                variant="success"
              />
              <MetricCard
                label="Worst Loss Streak"
                value="-3"
                icon="❄️"
                variant="danger"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Risk/Reward Section */}
      <div className="analytics-section">
        <h2 className="section-title">⚖️ Risk/Reward Analysis</h2>
        
        <div className="stats-grid-advanced">
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Profit Factor Breakdown</h3>
            </div>
            <div className="stats-section-body">
              <StatRow label="Gross Profit" value="₹1,85,230" variant="success" />
              <StatRow label="Gross Loss" value="-₹40,000" variant="danger" />
              <StatRow label="Profit Factor" value={stats.profitFactor} variant="highlight" />
              <StatRow label="Risk/Reward Ratio" value="1.88:1" />
            </div>
          </div>

          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Monthly Targets</h3>
            </div>
            <div className="stats-section-body">
              <MetricCard
                label="Monthly Goal"
                value="₹25,000"
                subValue="Target"
                icon="🎯"
              />
              <MetricCard
                label="Current P&L"
                value="₹18,450"
                subValue="This month"
                icon="📈"
                trend="up"
                variant="success"
              />
              <MetricCard
                label="Progress"
                value="73.8%"
                icon="📊"
                variant="warning"
              />
            </div>
          </div>

          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Session Performance</h3>
            </div>
            <div className="stats-section-body">
              <Sparkline 
                data={[10, 15, 12, 18, 22, 19, 25, 28, 24, 30]}
                color="var(--success)"
              />
              <StatRow label="Intraday High" value="+₹8,200" variant="success" />
              <StatRow label="Intraday Low" value="-₹2,100" variant="danger" />
              <StatRow label="Sessions Won" value="18/22" />
            </div>
          </div>
        </div>
      </div>

      {/* Trade Distribution Section */}
      <div className="analytics-section">
        <h2 className="section-title">📊 Trade Distribution Analysis</h2>
        
        <div className="distribution-grid">
          {/* P&L Histogram */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">P&L Distribution</h3>
            </div>
            <div className="stats-section-body">
              <Histogram 
                data={[
                  { label: '-250', count: 5 },
                  { label: '-200', count: 8 },
                  { label: '-150', count: 12 },
                  { label: '-100', count: 18 },
                  { label: '-50', count: 22 },
                  { label: '0', count: 35 },
                  { label: '+50', count: 28 },
                  { label: '+100', count: 22 },
                  { label: '+150', count: 15 },
                  { label: '+200', count: 10 },
                  { label: '+250', count: 6 },
                ]}
                title=""
                showValues
                binSize={50}
              />
            </div>
          </div>

          {/* Time Distribution */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Hourly Performance</h3>
            </div>
            <div className="stats-section-body">
              <TimeDistribution 
                data={[
                  { hour: 9, winRate: 72, trades: 45, avgPnl: 320 },
                  { hour: 10, winRate: 68, trades: 52, avgPnl: 280 },
                  { hour: 11, winRate: 65, trades: 38, avgPnl: 240 },
                  { hour: 12, winRate: 58, trades: 25, avgPnl: 180 },
                  { hour: 13, winRate: 62, trades: 30, avgPnl: 200 },
                  { hour: 14, winRate: 70, trades: 42, avgPnl: 310 },
                  { hour: 15, winRate: 75, trades: 55, avgPnl: 380 },
                ]}
                title=""
                view="hourly"
              />
            </div>
          </div>

          {/* Sector Breakdown */}
          <div className="stats-section full-width">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Segment Performance</h3>
            </div>
            <div className="stats-section-body">
              <SectorBreakdown 
                data={[
                  { name: 'NIFTY', value: 45230, count: 85 },
                  { name: 'BANKNIFTY', value: 38450, count: 72 },
                  { name: 'FINNIFTY', value: 28100, count: 52 },
                  { name: 'SENSEX', value: 33450, count: 38 },
                ]}
                title=""
                showPercentage
              />
            </div>
          </div>

          {/* Duration Analysis */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Trade Duration</h3>
            </div>
            <div className="stats-section-body">
              <DurationAnalysis 
                data={[
                  { range: 'Intraday (< 1h)', count: 156, avgPnl: 245, winRate: 72 },
                  { range: 'Short (1-4h)', count: 58, avgPnl: 420, winRate: 65 },
                  { range: 'Medium (4h-1d)', count: 25, avgPnl: 680, winRate: 60 },
                  { range: 'Swing (1-5d)', count: 8, avgPnl: 1200, winRate: 50 },
                ]}
                title=""
              />
            </div>
          </div>

          {/* Day of Week */}
          <div className="stats-section">
            <div className="stats-section-header">
              <h3 className="stats-section-title">Day Performance</h3>
            </div>
            <div className="stats-section-body">
              <TimeDistribution 
                data={[
                  { hour: 0, winRate: 45, trades: 18, avgPnl: -50 },
                  { hour: 1, winRate: 62, trades: 32, avgPnl: 180 },
                  { hour: 2, winRate: 70, trades: 45, avgPnl: 320 },
                  { hour: 3, winRate: 75, trades: 52, avgPnl: 380 },
                  { hour: 4, winRate: 68, trades: 48, avgPnl: 280 },
                  { hour: 5, winRate: 58, trades: 35, avgPnl: 150 },
                  { hour: 6, winRate: 40, trades: 17, avgPnl: -120 },
                ]}
                title=""
                view="daily"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics